import React, { useEffect, useCallback, useState, useMemo } from 'react';
import {
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import debounce from 'lodash.debounce';
import moment, { Moment } from 'moment';
import { Intl } from '@erecruit/core';
import { useRootRoute, Authorize, AuthorizedRoute } from '@erecruit/modules';
import {
  DayPicker,
  Button,
  Icon,
  Flex,
  Spinner,
  getDaysInWeek,
} from '@erecruit/ui-kit';
import { Resources, AppPermissions } from '@erecruit/types';

import {
  default as ShiftSearch,
  useShiftCalendarCount,
} from '../components/ShiftSearch';
import Insight from '../App/components/InsightPortal';
import PositionRecord from '../components/PositionRecord';
import { ShiftRecord } from '../components/ShiftRecord';
import { MatchRecord } from '../components/MatchRecord';
import { addNewShiftRoute } from '../routes';

const { FormattedMessage } = Intl;

const mapSelectedDaysToFilter = (selectedDays: Moment[]): Date[] =>
  selectedDays.map(day => day.toDate());

const Shifts = () => {
  const [isMountedHistory, setIsMountedHistory] = useState(false);
  const history = useHistory();
  const { pathname } = useLocation();
  const rootRoute = useRootRoute();
  const routeMatch = useRouteMatch<{ shiftId: string }>();

  if (!isMountedHistory && !routeMatch.isExact) {
    if (pathname.includes('shifts/position')) {
      history.push(pathname.replace('shifts/position', 'position'));
    } else if (pathname.includes('shifts/match')) {
      history.push(pathname.replace('shifts/match', 'match'));
    } else {
      history.push(pathname.replace('shifts', 'shift'));
    }
  }

  const [searchParams, setSearchParams] = useState<
    Partial<Resources.ShiftSearchFilter>
  >({
    startDates: mapSelectedDaysToFilter(getDaysInWeek(moment())),
  });

  const [shiftCalendarCountInfos, isLoadingShiftCount] = useShiftCalendarCount(
    searchParams,
  );

  const onDayPickerChange = useCallback((selectedDays: Moment[]) => {
    if (selectedDays.length === 0) {
      setSearchParams({});
    } else {
      setSearchParams({ startDates: mapSelectedDaysToFilter(selectedDays) });
    }
  }, []);

  const onDayPickerChangeDebounced = useCallback(
    debounce(onDayPickerChange, 1000),
    [],
  );

  const dayPickerChangeHandler = useCallback(
    (selectedDays: Moment[]) => {
      if (selectedDays.length === 7) {
        return onDayPickerChange(selectedDays);
      } else {
        return onDayPickerChangeDebounced(selectedDays);
      }
    },
    [onDayPickerChangeDebounced, onDayPickerChange],
  );

  const onAddNewShift = useCallback(() => {
    history.push({
      pathname: addNewShiftRoute,
      state: {
        referrer: pathname,
      },
    });
  }, [history, pathname]);

  const actions = useMemo(
    () => [
      <Authorize
        key="addShift"
        permission={`${AppPermissions.AddNewShift} & ${AppPermissions.ViewPositions}`}
      >
        <Button type="primary" onClick={onAddNewShift}>
          <Flex align="center">
            <FormattedMessage id={messageIds.vms.shifts.addNewShift} />
            <Icon type="plus" size="small" color="bodyTertiary" />
          </Flex>
        </Button>
      </Authorize>,
    ],
    [onAddNewShift],
  );

  useEffect(() => {
    return history.listen(() => {
      setIsMountedHistory(true);
    });
  }, [history]);

  return (
    <>
      <ShiftSearch
        actions={actions}
        exportPermission={AppPermissions.ExportShifts}
        additionalContent={
          <Spinner spinning={isLoadingShiftCount}>
            <DayPicker
              onChange={dayPickerChangeHandler}
              infos={shiftCalendarCountInfos}
            />
          </Spinner>
        }
        outerParams={searchParams}
      />
      {isMountedHistory && (
        <Switch>
          <AuthorizedRoute
            exact
            permission={`${AppPermissions.ViewShifts} & ${AppPermissions.ViewShift}`}
            path={`/shifts/:id`}
            render={props => (
              <Insight referer={rootRoute}>
                <ShiftRecord {...props} />
              </Insight>
            )}
          />
          <AuthorizedRoute
            exact
            permission={`${AppPermissions.ViewMatches} & ${AppPermissions.ViewMatch}`}
            path={`/shifts/match/:id`}
            render={props => (
              <Insight referer={rootRoute}>
                <MatchRecord {...props} />
              </Insight>
            )}
          />
          <AuthorizedRoute
            permission={`${AppPermissions.ViewPositions} & ${AppPermissions.ViewPosition}`}
            path={`/shifts/position/:id/:matchStatus?`}
            render={props => (
              <Insight referer={rootRoute}>
                <PositionRecord {...props} />
              </Insight>
            )}
          />
        </Switch>
      )}
    </>
  );
};

export default Shifts;
